import React, { Suspense, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ElasticTheme as Theme } from '@dev/ee-components';
import WebFont from 'webfontloader';
import styled from 'styled-components';
import { session } from 'store2';
import { IconButton, ClickAwayListener } from '@material-ui/core';
import { Settings as SettingsIcon } from '@material-ui/icons';
import { parse, stringify } from 'query-string';
import { I18nextProvider } from 'react-i18next';
import { Provider as ReduxProvider } from 'react-redux';
import { Router as RouterProvider } from 'react-router-dom';
import { History } from '@dev/history';
import { Helmet } from 'react-helmet';
import { omit, isEmpty } from 'lodash-es';
import { useBrowserFingerprint } from '@dev/ee-fingerprint';
import TagManager, { TagManagerArgs } from '@sooro-io/react-gtm-module';

import Router from '@api/services/Router';
import Layout from '@api/services/Layout';
import Auth from '@api/services/Authentication';
import { SessionStartAuthWithDisposableToken, SessionSuccessAuth } from '@api/store/session/actions';
import { AccountGetCustomBrandingStart, AccountStartLoading, getAccountStore } from '@api/store/account/actions';
import { LoadProductList } from '@api/store/products/actions';
import translations from '@api/i18n';
import { boot } from '@shared-store/shared-store';
import SimpleLoader from '@api/services/PageLoader';
import ScrollIntoView from '@api/services/ScrollIntoView';
import Dialog from '@api/services/Dialog';
import { SetIsBootedAsChild, SetRouterBasePath } from '@api/store/config/actions';
import { OptionalIntercomProvider } from './services/IntercomProvider';
import { ToastsProvider } from './services/ToastsProvider';
import { useNotifier } from './services/hooks/useNotifier';
import { getSessionAuth } from './store/session/actions';
import rootSaga from './sagas';
import { ApplicationState, reducers } from './store';
import { StorageKeysEnum } from './services/dictionaries/StorageKeys';
import { useGTMLoginEvent } from './services/hooks/useGTMLoginEvent';
import { ProductType } from '@dev/ee-api/ee-api-models';
import { Styles } from './services/Styles';
import { replace } from 'redux-first-history';

const styles = {
    body: {
        fontFamily: 'Mulish',
        fontSize: '16px',
        color: Theme.palette.primary.main,
        overflow: 'auto',
        padding: 0,
        margin: 0,
        width: 'auto',
        height: '100vh',
        background: '#fff',
    },
};

const DebugInfoBox = styled.div`
    padding: 0.5em;
    font-size: 0.8em;
    position: fixed;
    bottom: 50%;
    right: 20px;
    background: rgb(253, 213, 203);
    border: 1px solid rgb(167, 1, 60);
`;

interface ApplicationProps {
    routerBasename?: string;
    history?: History;
    isBootedAsChild?: boolean;
}

interface AppProps {
    routerBasename?: string;
    i18n?: any;
    history?: History;
    isBootedAsChild?: boolean;
}

const tagManagerArgs: TagManagerArgs = {
    gtmId: GTMID,
    source: GTM_SERVER,
};

//@ts-expect-error datalayer
if (DEVMODE === 'PRODUCTION' && !!GTMID && !window.dataLayer) {
    TagManager.initialize(tagManagerArgs);
}

const ApplicationCore = (): JSX.Element => {
    const account = useSelector(getAccountStore);
    const showDebugToggler = account?.Profile?.Email.includes('@elasticemail.com');
    const [showDebugInfo, setShowDebugInfo] = useState(false);

    useNotifier();

    const toggleDebugInfo = (): void => {
        setShowDebugInfo(!showDebugInfo);
    };

    const onStopImpersonationClick = (): void => {
        session.remove('subaccountemailtouse');
        window.location.reload();
    };

    const renderDebugInfo = (): JSX.Element => {
        const impersonateAs = session.get('subaccountemailtouse');

        return (
            <ClickAwayListener onClickAway={toggleDebugInfo}>
                <DebugInfoBox>
                    mode: {DEVMODE} <br />
                    version: {VERSION} <br />
                    git revision:{' '}
                    <a
                        href={`https://repo.elasticemail.in/dev/emailapi-dashboard/commit/${COMMITHASH}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {REVISION}
                    </a>
                    <br />
                    branch: {BRANCH} <br />
                    endpoint: {APIPATH} <br />
                    {impersonateAs && (
                        <>
                            Impersonate as: {impersonateAs} <br />
                        </>
                    )}
                    {impersonateAs && (
                        <>
                            <a onClick={onStopImpersonationClick} href="javascript:void(0)">
                                Stop impersonating{' '}
                            </a>
                        </>
                    )}
                </DebugInfoBox>
            </ClickAwayListener>
        );
    };

    const renderDebugInfoToggler = (): JSX.Element => {
        return (
            <IconButton onClick={toggleDebugInfo} style={{ position: 'fixed', right: '10px', bottom: '10px' }}>
                <SettingsIcon />
            </IconButton>
        );
    };

    return (
        <OptionalIntercomProvider>
            <Helmet>
                <title>EmailAPI - Dashboard</title>
            </Helmet>
            <ScrollIntoView>
                <Layout>
                    <Router />
                    <Dialog />
                </Layout>
            </ScrollIntoView>
            {showDebugInfo && renderDebugInfo()}
            {showDebugToggler && renderDebugInfoToggler()}
        </OptionalIntercomProvider>
    );
};

const ConnectedApp = (props: ApplicationProps): JSX.Element => {
    const dispatch = useDispatch();
    const { routerBasename, isBootedAsChild } = props;
    const authorized = useSelector(getSessionAuth);
    let queryParams = parse(window.location.search, { decode: false });
    if (!props.isBootedAsChild) {
        WebFont.load({
            google: {
                families: ['Nunito:400,600,700', 'Mulish', 'sans-serif'],
            },
        });
    }

    useEffect(() => {
        dispatch(AccountGetCustomBrandingStart());

        for (const i in styles.body) {
            document.body.style[i] = styles.body[i];
        }

        if (routerBasename) {
            dispatch(SetRouterBasePath(routerBasename));
        }

        if (isBootedAsChild) {
            dispatch(SetIsBootedAsChild(isBootedAsChild));
        }
        if (queryParams.i) {
            const publicAccountId = queryParams.i as string;

            sessionStorage.setItem('subaccountemailtouse', publicAccountId);
            sessionStorage.setItem('parentName', 'Admin');

            queryParams = omit(queryParams, ['i']);

            if (isEmpty(queryParams)) {
                dispatch(replace({ search: stringify(queryParams) }));
                window.location.reload();
            }
        }

        if (queryParams.t) {
            const token = queryParams.t as string;
            queryParams = omit(queryParams, ['t']);

            if (!isEmpty(queryParams)) {
                dispatch(replace({ search: stringify(queryParams) }));
            }

            dispatch(SessionStartAuthWithDisposableToken(token));
        }

        dispatch(replace({ search: stringify(queryParams) }));

        if (Auth.isAuthenticated()) {
            dispatch(SessionSuccessAuth());
            dispatch(AccountStartLoading());
        }
    }, []);

    useEffect(() => {
        if (Auth.isAuthenticated()) {
            dispatch(LoadProductList());
        }
    }, [authorized]);

    useGTMLoginEvent(ProductType.API);

    return (
        <ToastsProvider>
            <ApplicationCore />
        </ToastsProvider>
    );
};

export const App = (props: AppProps): JSX.Element => {
    const { history: reduxHistory, store } = boot<ApplicationState>({
        basename: props.routerBasename,
        isStandAlone: true,
        rootSaga,
        reducers,
        GAKEY: GAKEY,
        DEVMODE: DEVMODE,
    });

    const fingerprint = useBrowserFingerprint();

    if (fingerprint) {
        localStorage.setItem(StorageKeysEnum.Fingerprint, fingerprint);
    }

    const routerHistory = props.history ? props.history : reduxHistory;

    return (
        <ReduxProvider store={store}>
            <Suspense fallback={<SimpleLoader />}>
                <I18nextProvider i18n={translations}>
                    <RouterProvider history={routerHistory}>
                        <Styles>
                            <ConnectedApp
                                routerBasename={props.routerBasename}
                                history={routerHistory}
                                isBootedAsChild={props.isBootedAsChild}
                            />
                        </Styles>
                    </RouterProvider>
                </I18nextProvider>
            </Suspense>
        </ReduxProvider>
    );
};

export default App;
